import { createFileRoute, Link, useParams } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import { Edit, Shield, TrendingUp } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import { EmptyState } from '@/components/ui/empty-state';
import {
  useCheckpointsListQuery,
  type Checkpoint,
  listCheckpointAssessments,
  type AssessmentSummary,
} from '@/lib/api/checkpoints';
import {
  VendorScoringTable,
  type VendorScoringTableRow,
} from '@/components/contract/vendor-scoring-table';
import { CheckpointScoringModal } from '@/components/modals/checkpoint-scoring-modal';
import { ContractHeader } from '@/components/contract/contract-header';
import { useEdlListQuery } from '@/lib/api/deliverables';
import { VendorRecordCard } from '@/components/contract/vendor-record-card';

/**
 * /contract/vendor — C20-133 prototype-parity rewrite (T-0103 / E06-F05).
 *
 * Visual contract: `screens-modules-tabs.js → contractVendor()` (lines
 * 538-644). Layout:
 *
 *   <ContractHeader actions="Trend view · Open scoring form" />
 *   <KPI strip>            Vendor · Avg score · Trend · Last-5-deliverables
 *   <DualAxisCallout>      E04-F06 — vendor self-scoring vs. Avero verification
 *                          ┌────────────┐┌────────────┐┌────────────┐┌────────────┐
 *                          │ Verified pass │ Divergent │ Failed   │ Unverified  │
 *                          └────────────┘└────────────┘└────────────┘└────────────┘
 *   <VendorScoringTable>   Checkpoint × Final score + Status + Trend sparkline
 *
 * Wire-up matrix:
 *   - Checkpoints list           → useCheckpointsListQuery (T-0066)
 *   - Per-checkpoint rounds      → useQueries(listCheckpointAssessments) (T-0067)
 *   - Verified pass / Divergent / Failed / Unverified buckets
 *                                → derived from latest assessment per checkpoint
 *                                  via deterministic UI deriver (NEVER the LLM
 *                                  per CLAUDE.md hard rule #7)
 *   - "Open scoring form"        → data-create="checkpoint-assessment" (T-0114)
 *   - "Trend view"               → defers to per-checkpoint trend (sparkline)
 *
 * Backend gaps surfaced as Pending (never faked):
 *   - Vendor name + "Last 5 deliverables" caption come from the engagement
 *     record + EDL list; we surface the EDL count plainly without inventing
 *     the prototype's "3 Revise & Resubmit · 2 Approved" mix.
 *   - "Declining vs. improving" trend caption derived from sparkline (last
 *     two scores). When fewer than 2 rounds exist, shown as "—".
 */
export const Route = createFileRoute('/engagements/$engagementId/contract/vendor')({
  component: ContractVendorTab,
});

function ContractVendorTab() {
  const { engagementId } = useParams({ from: '/engagements/$engagementId/contract/vendor' });
  const checkpointsQuery = useCheckpointsListQuery(engagementId, { status: 'active' });
  const [scoringTarget, setScoringTarget] = useState<Checkpoint | null>(null);
  const checkpoints = checkpointsQuery.data?.items ?? [];
  const edl = useEdlListQuery(engagementId);

  // Fan out one query per checkpoint for its full assessment history.
  // useQueries gives us a stable parallel-fetch surface that respects
  // React Query's de-dup, caching, and refetch policy.
  const assessmentQueries = useQueries({
    queries: checkpoints.map((c) => ({
      queryKey: ['checkpoint-assessments', c.checkpoint_id] as const,
      queryFn: () => listCheckpointAssessments(c.checkpoint_id),
      staleTime: 30_000,
    })),
  });

  const allAssessmentsLoaded = assessmentQueries.every((q) => !q.isLoading);

  // Join checkpoint → its sorted assessments into table rows. Sorting by
  // round_seq is the canonical ordering the deriver and the sparkline
  // both depend on.
  const rows = useMemo<VendorScoringTableRow[]>(() => {
    return checkpoints.map((c, idx) => {
      const items = assessmentQueries[idx]?.data?.items ?? [];
      const sorted = [...items].sort((a, b) => a.round_seq - b.round_seq);
      return { checkpoint: c, assessments: sorted };
    });
  }, [checkpoints, assessmentQueries]);

  const summary = useMemo(() => summarize(rows), [rows]);

  return (
    <div className="space-y-6">
      <ContractHeader
        engagementId={engagementId}
        active="vendor"
        meta={
          <span>
            {summary.scoredCheckpoints} of {summary.totalCheckpoints}{' '}
            {summary.totalCheckpoints === 1 ? 'checkpoint' : 'checkpoints'} scored ·{' '}
            {edl.data?.entries.length ?? '—'} deliverables tracked
          </span>
        }
        actions={
          <>
            <Button asChild variant="outline" size="sm">
              <Link to="/engagements/$engagementId/contract/checkpoints" params={{ engagementId }}>
                <TrendingUp className="h-4 w-4" aria-hidden="true" />
                Checkpoint detail
              </Link>
            </Button>
            <Button
              type="button"
              size="sm"
              className="bg-accent-amber-600 text-paper-0 hover:bg-accent-amber-700"
              disabled={checkpoints.length === 0}
              onClick={() => {
                const next = nextToScore(rows);
                if (next) setScoringTarget(next);
              }}
            >
              <Edit className="h-4 w-4" aria-hidden="true" />
              Open scoring form
            </Button>
          </>
        }
      />

      {checkpointsQuery.isLoading ? (
        <div className="flex items-center gap-2 text-body-sm text-ink-500">
          <Spinner /> Loading checkpoints…
        </div>
      ) : checkpointsQuery.error ? (
        <ErrorMessage error={checkpointsQuery.error} />
      ) : checkpoints.length === 0 ? (
        <EmptyState
          title="No checkpoints configured"
          description="Once IV&V checkpoints exist for this engagement, vendor scoring rounds appear here as a per-checkpoint trend. Configure checkpoints under IV&V checkpoints."
        />
      ) : (
        <>
          <VendorRecordCard engagementId={engagementId} />
          <SummaryStrip summary={summary} loading={!allAssessmentsLoaded} />
          <DualAxisCallout summary={summary} />
          <Card>
            <CardContent className="p-0">
              <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-ink-100 px-5 py-3">
                <div>
                  <h2 className="text-h3 font-semibold text-ink-900">
                    Vendor scoring per IV&V checkpoint
                  </h2>
                  <p className="text-meta text-ink-500">
                    Score · status · trend. Definitions live in{' '}
                    <a
                      href={`/engagements/${engagementId}/contract/checkpoints`}
                      className="text-ink-700 underline underline-offset-2"
                    >
                      IV&V checkpoints
                    </a>
                    .
                  </p>
                </div>
              </div>
              <div className="p-4">
                <VendorScoringTable rows={rows} onScore={(cp) => setScoringTarget(cp)} />
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {scoringTarget ? (
        <CheckpointScoringModal
          checkpoint={scoringTarget}
          open={scoringTarget !== null}
          onOpenChange={(next) => {
            if (!next) setScoringTarget(null);
          }}
        />
      ) : null}
    </div>
  );
}

// ===========================================================================
// Summary derivation
// ===========================================================================

/**
 * The checkpoint "Open scoring form" should land on: one with a round in
 * progress first; then the earliest-due checkpoint with no submitted round;
 * then the first checkpoint.
 */
function nextToScore(rows: VendorScoringTableRow[]): Checkpoint | null {
  const inProgress = rows.find((r) => r.assessments.some((a) => a.status === 'in_progress'));
  if (inProgress) return inProgress.checkpoint;
  const unscored = rows
    .filter((r) => !r.assessments.some((a) => a.status === 'submitted'))
    .sort((a, b) =>
      (a.checkpoint.due_date ?? '9999-12-31').localeCompare(b.checkpoint.due_date ?? '9999-12-31'),
    );
  return unscored[0]?.checkpoint ?? rows[0]?.checkpoint ?? null;
}

interface VendorSummary {
  scoredCheckpoints: number;
  totalCheckpoints: number;
  postureCounts: {
    green: number;
    yellow: number;
    red: number;
    grey: number;
  };
  averagePostureScore: number | null;
  trend: 'improving' | 'declining' | 'flat' | 'unknown';
}

function summarize(rows: VendorScoringTableRow[]): VendorSummary {
  const counts = { green: 0, yellow: 0, red: 0, grey: 0 };
  let scored = 0;
  let total = 0;
  let improving = 0;
  let declining = 0;
  for (const r of rows) {
    const latest = r.assessments[r.assessments.length - 1];
    if (!latest) continue;
    if (latest.overall_posture) {
      total += postureScore(latest);
      scored += 1;
    }
    switch (latest.overall_posture) {
      case 'green':
        counts.green += 1;
        break;
      case 'yellow_divergent':
        counts.yellow += 1;
        break;
      case 'red':
        counts.red += 1;
        break;
      case 'grey':
      case null:
      default:
        counts.grey += 1;
        break;
    }
    // Trend per checkpoint — direction of last delta (when ≥ 2 rounds).
    if (r.assessments.length >= 2) {
      const prev = r.assessments[r.assessments.length - 2];
      const a = postureScore(latest);
      const b = prev ? postureScore(prev) : 0;
      if (a > b) improving += 1;
      else if (a < b) declining += 1;
    }
  }
  let trend: VendorSummary['trend'] = 'unknown';
  if (improving + declining > 0) {
    if (declining > improving) trend = 'declining';
    else if (improving > declining) trend = 'improving';
    else trend = 'flat';
  }
  return {
    scoredCheckpoints: scored,
    totalCheckpoints: rows.length,
    postureCounts: counts,
    averagePostureScore: scored > 0 ? total / scored : null,
    trend,
  };
}

function postureScore(a: AssessmentSummary): number {
  switch (a.overall_posture) {
    case 'green':
      return 3;
    case 'yellow_divergent':
      return 2;
    case 'red':
      return 1;
    case 'grey':
    case null:
    default:
      return 0;
  }
}

// ===========================================================================
// Top KPI strip — `kpi()` parity
// ===========================================================================

function SummaryStrip({ summary, loading }: { summary: VendorSummary; loading: boolean }) {
  const avg = summary.averagePostureScore;
  const trendLabel =
    summary.trend === 'declining'
      ? 'Declining'
      : summary.trend === 'improving'
        ? 'Improving'
        : summary.trend === 'flat'
          ? 'Flat'
          : '—';
  const trendCaption =
    summary.trend === 'unknown'
      ? 'Need at least 2 rounds to derive a trend'
      : `${summary.totalCheckpoints} ${summary.totalCheckpoints === 1 ? 'checkpoint' : 'checkpoints'} compared round-over-round`;
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
      <SummaryCard
        label="Checkpoints scored"
        value={`${summary.scoredCheckpoints} / ${summary.totalCheckpoints}`}
        sub="with at least one round"
      />
      <SummaryCard
        label="Average posture"
        value={loading ? '…' : avg !== null ? avg.toFixed(2) : '—'}
        sub="3 = Green · 2 = Yellow · 1 = Red"
      />
      <SummaryCard
        label="Trend"
        value={loading ? '…' : trendLabel}
        sub={trendCaption}
        tone={summary.trend === 'declining' ? 'at-risk' : undefined}
      />
      <SummaryCard
        label="Latest posture mix"
        value={loading ? '…' : `${summary.postureCounts.green} green`}
        sub={`${summary.postureCounts.yellow} yellow · ${summary.postureCounts.red} red · ${summary.postureCounts.grey} grey`}
        tone={
          summary.postureCounts.red > 0
            ? 'critical'
            : summary.postureCounts.yellow > 0
              ? 'at-risk'
              : 'healthy'
        }
      />
    </div>
  );
}

function SummaryCard({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: string;
  sub: string;
  tone?: 'healthy' | 'at-risk' | 'critical';
}) {
  const accent =
    tone === 'critical'
      ? 'border-l-posture-critical'
      : tone === 'at-risk'
        ? 'border-l-accent-amber-600'
        : tone === 'healthy'
          ? 'border-l-posture-healthy'
          : 'border-l-ink-100';
  const valueClass =
    tone === 'critical'
      ? 'text-posture-critical'
      : tone === 'at-risk'
        ? 'text-accent-amber-700'
        : 'text-ink-900';
  return (
    <Card className={`border-l-[3px] ${accent}`}>
      <CardContent className="space-y-1 p-4">
        <div className="text-meta uppercase tracking-wide text-ink-500">{label}</div>
        <div className={`text-h2 font-semibold ${valueClass}`}>{value}</div>
        <div className="text-meta text-ink-500">{sub}</div>
      </CardContent>
    </Card>
  );
}

// ===========================================================================
// Dual-axis callout (prototype E04-F06 callout, lines 588-613)
// ===========================================================================

function DualAxisCallout({ summary }: { summary: VendorSummary }) {
  // The prototype's "Verified pass / Divergent / Failed / Unverified"
  // tiles are derived from per-row vendor_claim × avero_verification —
  // the assessment-level surface here exposes `overall_posture` only.
  // We treat overall_posture as the rollup proxy:
  //   green            → Verified pass
  //   yellow_divergent → Divergent
  //   red              → Failed
  //   grey / null      → Unverified
  return (
    <Card className="border-l-[3px] border-l-accent-amber-600">
      <CardContent className="space-y-4 p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-h3 font-semibold text-ink-900">
              <Shield className="h-4 w-4 text-accent-amber-700" aria-hidden="true" />
              Vendor self-scoring is not enough
            </div>
            <p className="text-meta text-ink-500">
              Vendor's posture vs. Avero's independent verification. Divergent items surface in
              pulse + synthesis reports as accountability cases.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <RollupTile
            label="Verified pass"
            value={summary.postureCounts.green}
            background="bg-posture-healthy-tint"
            border="border-posture-healthy"
            color="text-posture-healthy"
          />
          <RollupTile
            label="Divergent"
            value={summary.postureCounts.yellow}
            background="bg-accent-amber-50"
            border="border-accent-amber-600"
            color="text-accent-amber-700"
          />
          <RollupTile
            label="Failed"
            value={summary.postureCounts.red}
            background="bg-posture-critical-tint"
            border="border-posture-critical"
            color="text-posture-critical"
          />
          <RollupTile
            label="Unverified"
            value={summary.postureCounts.grey}
            background="bg-paper-50"
            border="border-dashed border-ink-200"
            color="text-ink-500"
          />
        </div>
      </CardContent>
    </Card>
  );
}

function RollupTile({
  label,
  value,
  background,
  border,
  color,
}: {
  label: string;
  value: number;
  background: string;
  border: string;
  color: string;
}) {
  return (
    <div className={`rounded-card border ${border} ${background} p-3`}>
      <div className="text-meta font-medium uppercase tracking-wide text-ink-700">{label}</div>
      <div className={`mt-1 text-h2 font-semibold tabular-nums ${color}`}>{value}</div>
    </div>
  );
}

export { ContractVendorTab };
